import { useCallback, useRef } from 'react'
import XLSX from 'xlsx-js-style'

const useExportToExcel = () => {
  const tableRef = useRef(null)

  const exportTableToExcel = useCallback((filename) => {
    if (!tableRef.current) return

    // Get the table element
    const table = tableRef.current

    // Create a new workbook
    const wb = XLSX.utils.book_new()

    // Build the data array
    const data = [[filename], []] // First row: filename, second row: empty

    // Track header row index and column count
    let headerRowIndex = 2 // 0-indexed, after filename and empty row
    let columnCount = 0

    // Get column widths from thead th elements
    const columnWidths = []
    const theadHeaders = table.querySelectorAll('thead th')
    theadHeaders.forEach((th) => {
      const width = th.getAttribute('data-excel-export-width')
      columnWidths.push(width ? { wch: parseInt(width, 10) } : { wch: 10 })
    })

    // Loop through the rows and cells of the table
    table.querySelectorAll('tr').forEach((row) => {
      const rowArray = []
      row.querySelectorAll('td, th').forEach((cell) => {
        // Check if the cell contains a select element
        const selectElement = cell.querySelector('select')
        if (selectElement) {
          // Get the selected option and its value
          const selectedOption =
            selectElement.options[selectElement.selectedIndex]
          const selectedValue = selectedOption ? selectedOption.textContent : ''
          rowArray.push(selectedValue)
        } else {
          rowArray.push(cell.textContent)
        }
      })
      if (rowArray.length > columnCount) {
        columnCount = rowArray.length
      }
      data.push(rowArray)
    })

    // Create worksheet from data
    const ws = XLSX.utils.aoa_to_sheet(data)

    // Set column widths
    if (columnWidths.length > 0) {
      ws['!cols'] = columnWidths
    }

    // Define border style
    const border = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    }

    // Define white border style to hide borders for first two rows
    const whiteBorder = {
      top: { style: 'thin', color: { rgb: 'FFFFFF' } },
      bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
      left: { style: 'thin', color: { rgb: 'FFFFFF' } },
      right: { style: 'thin', color: { rgb: 'FFFFFF' } },
    }

    // Ensure the first two rows (filename and empty) have white borders
    for (let col = 0; col < columnCount; col++) {
      const cell0 = XLSX.utils.encode_cell({ r: 0, c: col })
      const cell1 = XLSX.utils.encode_cell({ r: 1, c: col })

      // Create cell if it doesn't exist
      if (!ws[cell0]) ws[cell0] = { v: '', t: 's' }
      if (!ws[cell1]) ws[cell1] = { v: '', t: 's' }

      ws[cell0].s = { border: whiteBorder }
      ws[cell1].s = { border: whiteBorder }
    }

    // Apply styles to cells
    const range = XLSX.utils.decode_range(ws['!ref'])
    for (let row = headerRowIndex; row <= range.e.r; row++) {
      for (let col = 0; col < columnCount; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col })
        if (!ws[cellAddress]) {
          ws[cellAddress] = { v: '', t: 's' }
        }

        // Apply border to all data cells
        ws[cellAddress].s = { border }

        // Apply bold to header row (first row of table data)
        if (row === headerRowIndex) {
          ws[cellAddress].s = {
            border,
            font: { bold: true },
          }
        }
      }
    }

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')

    // Generate a FileSaver dialog to save the Excel file with the specified filename
    XLSX.writeFile(wb, `${filename}.xlsx`)
  }, [])

  return { tableRef, exportTableToExcel }
}

export default useExportToExcel
